import React from 'react';
import styled from 'styled-components';

const EyeIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const PlaceNames = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const PlaceTag = styled.span`
  font-size: 11px;
  padding: 2px 7px;
  background: #EEF4FF;
  color: #1e5faa;
  border-radius: 99px;
  white-space: nowrap;
`;

const MoreTag = styled.span`
  font-size: 11px;
  padding: 2px 7px;
  background: #F2EDE8;
  color: #7A6A60;
  border-radius: 99px;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 30px 2fr 1.2fr 1.2fr 70px 1.8fr 110px 100px 90px;
  align-items: center;
  padding: 11px 16px;
  border-bottom: 1px solid #faf8f6;
  cursor: pointer;
  transition: background 0.1s;
  &:last-child {
    border-bottom: none;
  }
  &:hover {
    background: #faf8f6;
  }
`;

const CompanyName = styled.div`
  font-size: 13.5px;
  font-weight: 600;
  color: #16110d;
  margin-bottom: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CompanySub = styled.div`
  font-size: 11.5px;
  color: #b8aaa2;
`;

const AbnText = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: #16110d;
  font-family: monospace;
  margin-bottom: 3px;
`;

const ContactEmail = styled.div`
  font-size: 11.5px;
  color: #16110d;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 160px;
`;

const PlaceCount = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #1e5faa;
`;

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 8px;
  border-radius: 99px;
  font-size: 11px;
  font-weight: 600;
  background: ${({ bg }) => bg};
  color: ${({ clr }) => clr};
`;

const BankSub = styled.div`
  font-size: 10.5px;
  color: #b8aaa2;
  margin-top: 2px;
`;

const ActionBtn = styled.button`
  cursor: pointer;
  border: 1px solid #e8ddd6;
  outline: none;
  border-radius: 6px;
  transition: all 0.14s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  width: 28px;
  height: 28px;
  padding: 0;
  background: #f2ede8;
  color: #7a6a60;
  &:hover {
    opacity: 0.8;
  }
`;

const ABN_TAG = {
  AVAILABLE: { bg: '#E8F5EE', clr: '#1A7A4A', label: '✓ Verified' },
  UNAVAILABLE: { bg: '#FEF0E0', clr: '#B06A00', label: 'Pending' },
};

const BANK_TAG = {
  SET: { bg: '#E8F5EE', clr: '#1A7A4A', label: '✓ Set' },
  NOT_SET: { bg: '#FCEAEA', clr: '#C42B2B', label: 'Not set' },
};

const STATE_ABBR = {
  'New South Wales': 'NSW',
  Victoria: 'VIC',
  Queensland: 'QLD',
  'Western Australia': 'WA',
  'South Australia': 'SA',
  'Australian Capital Territory': 'ACT',
  Tasmania: 'TAS',
  'Northern Territory': 'NT',
};

const getPrimaryContact = contacts => {
  if (!contacts || contacts.length === 0) return null;
  return (
    contacts.find(c => c.type === 'email_primary' && c.is_primary) ||
    contacts.find(c => c.is_primary) ||
    contacts[0]
  );
};

const CompanyRow = ({ company, onRowClick, onEdit }) => {
  const abn = ABN_TAG[company.abn_status] || ABN_TAG.UNAVAILABLE;
  const bank = BANK_TAG[company.bank_status] || BANK_TAG.NOT_SET;
  const contact = getPrimaryContact(company.contact);
  const stateLabel = STATE_ABBR[company.state] || company.state || '—';

  return (
    <Row onClick={() => onRowClick(company)}>
      <div></div>
      <div style={{ overflow: 'hidden' }}>
        <CompanyName title={company.name}>{company.name}</CompanyName>
        <CompanySub>
          {[company.city, company.state].filter(Boolean).join(', ')}
        </CompanySub>
      </div>
      <div>
        <AbnText>{company.abn || '—'}</AbnText>
        <Tag bg={abn.bg} clr={abn.clr}>
          {abn.label}
        </Tag>
      </div>
      <div>
        {contact ? (
          <ContactEmail title={contact.value}>{contact.value}</ContactEmail>
        ) : (
          <span style={{ fontSize: 11, color: '#B8AAA2' }}>—</span>
        )}
      </div>
      <div>
        <PlaceCount>{company.counts ?? 0}</PlaceCount>
      </div>
      <div>
        {company.places && company.places.length > 0 ? (
          <PlaceNames>
            {company.places.slice(0, 2).map(p => (
              <PlaceTag key={p.place_id}>{p.name}</PlaceTag>
            ))}
            {company.places.length > 2 && (
              <MoreTag>+{company.places.length - 2}</MoreTag>
            )}
          </PlaceNames>
        ) : (
          <span style={{ fontSize: 11, color: '#B8AAA2' }}>—</span>
        )}
      </div>
      <div>
        <Tag bg="#F2EDE8" clr="#7A6A60">
          {stateLabel}
        </Tag>
      </div>
      <div>
        <Tag bg={bank.bg} clr={bank.clr}>
          {bank.label}
        </Tag>
        {company.bank_status === 'SET' && company.city && (
          <BankSub>{company.city}</BankSub>
        )}
      </div>
      <div
        onClick={e => e.stopPropagation()}
        style={{ display: 'flex', gap: 4 }}
      >
        <ActionBtn title="View details" onClick={() => onRowClick(company)}>
          <EyeIcon />
        </ActionBtn>
        {onEdit && (
          <ActionBtn
            title="Edit company"
            onClick={() => onEdit(company.company_id)}
          >
            ✏
          </ActionBtn>
        )}
      </div>
    </Row>
  );
};

export default CompanyRow;
